import { createRef, useCallback, useEffect, useMemo, useState } from 'react';

import { isNotDefined } from '@enterprise/core';

import type { InViewReturnType } from './types/in-view-return.type';
import type { InViewOptions } from './types/in-view.options';

/**
 * React hook to track whether a DOM element is in the viewport using the Intersection Observer API.
 *
 * @template TElement - The type of the HTML element to observe.
 * @param options - Optional configuration for the observer and behavior.
 *   - `once`: If true, the observer will only trigger once when the element becomes visible.
 *   - Other properties are passed to the IntersectionObserver as options.
 * @returns An object containing:
 *   - `count`: Number of times the element has become visible.
 *   - `hasBeenVisible`: Whether the element has ever been visible.
 *   - `isVisible`: Whether the element is currently visible in the viewport.
 *   - `ref`: A React ref to be attached to the target element.
 *
 * @remarks
 * - The hook automatically disconnects the observer when the component unmounts or the observed element changes.
 * - The `ref` must be attached to the DOM element you want to observe.
 */
export const useInView = <TElement extends HTMLElement>(options?: InViewOptions): InViewReturnType<TElement> => {
  const once = useMemo(() => {
    return options?.once ?? false;
  }, [options]);

  const observerOptions = useMemo(() => {
    return options ?? { threshold: 0 };
  }, [options]);

  const elementRef = createRef<TElement>();

  const [{ count, hasBeenVisible, isVisible }, setData] = useState<InViewReturnType<TElement>>({
    count: 0,
    hasBeenVisible: false,
    isVisible: false,
    ref: elementRef,
  });

  const observerCallback = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      if (isNotDefined(entry)) {
        return;
      }

      const { isIntersecting } = entry;

      if (isIntersecting && count > 0 && once) {
        return;
      }

      setData(({ count: currentCount, ref }) => {
        currentCount = isIntersecting ? currentCount + 1 : currentCount;

        return {
          count: currentCount,
          hasBeenVisible: currentCount > 0,
          isVisible: isIntersecting,
          ref,
        };
      });
    },
    [count, once]
  );

  const observer = useMemo(() => {
    return new IntersectionObserver(observerCallback, observerOptions);
  }, [observerCallback, observerOptions]);

  useEffect(() => {
    const element = elementRef.current;

    if (isNotDefined(element)) {
      return;
    }

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, observer]);

  return {
    count,
    hasBeenVisible,
    isVisible,
    ref: elementRef,
  };
};
